<template>
  <article v-if="total" class="flex gap-4 p-4 bg-bgr rounded shadow">
    <aside class="flex items-center shrink-0">
      <WebccIcon name="site/co2-footprint" class="w-10 h-10" />
    </aside>

    <section class="flex flex-col gap-1">
      <h4 class="flex items-center gap-3 text-sm font-medium">
        {{ $t(`${trPrefix}title`, formattedValues) }}
        <WebccIcon
          v-tooltip="tooltip"
          name="site/info-new"
          class="w-4 h-4 text-txt-weak"
        />
      </h4>
      <small class="text-xs text-txt-weak">
        {{ $t(`${trPrefix}description`, formattedValues) }}
      </small>
    </section>
  </article>
</template>

<script setup lang="ts">
const trPrefix = 'www.components.views.details.CO2Footprint.'

const props = defineProps<{
  accommodation: TranslatedAccommodation
  trip: TripUpdate
}>()

const { t } = useI18n()
const { formatNumber } = useNumberFormat()

const duration = computed(() => getTripDuration(props.trip) || DURATION_DEFAULT)
const ecomodation = computed(() => props.accommodation.ecomodation)
const perNight = computed(() => ecomodation.value?.co2FootprintPerNight ?? 0)
const total = computed(() => perNight.value * duration.value)

/**
 * Rounding (commercially) the CO2 footprint total amount, but not the value per night as it looks cleaner.
 * Because if we round 0.31 kg to 0.3 kg per night, it will not correspond to the total of 4.3 kg over 14 nights.
 *
 * @see https://jira.migros.net/browse/IHGWEBCC-1719
 */
const formattedValues = computed(() => ({
  total: formatNumber(total.value, 1),
  perNight: formatNumber(perNight.value, 2),
  duration: formatNumber(duration.value),
}))

const tooltip = useTooltip(() => t(`${trPrefix}tooltip`, formattedValues.value))
</script>
